import * as layout from '../../components/markLayout.module.css';
import { MarkButton, MarkInput, MarkSelect, MarkLabel } from '../../components/MarkControls';
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Section from '@hero/ui-kit/layout/Section';
import H from '@hero/ui-kit/typography/H';
import P from '@hero/ui-kit/typography/P';
import type { TemplateTargetKeys, Channel } from '../../types';
import BlockEditor from '../../components/BlockEditor';
const DevTemplateEditor: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const targetType = searchParams.get('target_type');
    const rawChannel = searchParams.get('channel') || 'email';
    const channel: Channel = rawChannel === 'sms' ? 'sms' : 'email';
    const readOnly = searchParams.get('read_only') === 'true';
    const target = buildTarget(searchParams);
    if (!target) {
        return (
            <Section subtheme={['regular', 'background']} className={layout.form} noDefaultPadding>
                <H role="h5" noDefaultMargin>
                    Dev Template Editor
                </H>
                <P
                    noDefaultMargin
                    size="small"
                    styles={{
                        color: 'var(--mark-muted)',
                        margin: '0.8rem 0'
                    }}
                >
                    Fill in the target to load a template.
                </P>
                <TargetForm
                    onSubmit={(params) => {
                        setSearchParams(params);
                    }}
                />
            </Section>
        );
    }
    return (
        <Section subtheme={['regular', 'background']} className={layout.form} noDefaultPadding>
            <H role="h5" noDefaultMargin>
                Dev Template Editor
            </H>
            <P
                noDefaultMargin
                size="small"
                styles={{
                    color: 'var(--mark-muted)',
                    margin: '0.8rem 0'
                }}
            >
                Target: {targetType} | Channel: {channel} | Mode: {readOnly ? 'read-only' : 'edit'}
            </P>
            <BlockEditor target={target} channel={channel} readOnly={readOnly} />
        </Section>
    );
};
const buildTarget = (params: URLSearchParams): TemplateTargetKeys | null => {
    const targetType = params.get('target_type');
    if (targetType === 'blast_variant') {
        const blastId = Number(params.get('blast_id'));
        const variantLabel = params.get('variant_label');
        if (!blastId || !variantLabel) return null;
        return { target_type: 'blast_variant', blast_id: blastId, variant_label: variantLabel };
    }
    if (targetType === 'drip_step') {
        const dripId = Number(params.get('drip_id'));
        const stepId = params.get('step_id');
        if (!dripId || !stepId) return null;
        const rawVersion = params.get('version_number');
        const versionNumber = rawVersion ? Number(rawVersion) : undefined;
        if (versionNumber !== undefined && !Number.isFinite(versionNumber)) return null;
        return {
            target_type: 'drip_step',
            drip_id: dripId,
            step_id: stepId,
            ...(versionNumber !== undefined ? { version_number: versionNumber } : {})
        };
    }
    return null;
};
type TargetFormProps = {
    onSubmit: (params: Record<string, string>) => void;
};
const TargetForm: React.FC<TargetFormProps> = ({ onSubmit }) => {
    const [targetType, setTargetType] = useState('blast_variant');
    const [blastId, setBlastId] = useState('');
    const [variantLabel, setVariantLabel] = useState('');
    const [dripId, setDripId] = useState('');
    const [stepId, setStepId] = useState('');
    const [versionNumber, setVersionNumber] = useState('');
    const [channel, setChannel] = useState('email');
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const params: Record<string, string> = { target_type: targetType, channel };
        if (targetType === 'blast_variant') {
            params.blast_id = blastId;
            params.variant_label = variantLabel;
        } else {
            params.drip_id = dripId;
            params.step_id = stepId;
            if (versionNumber) params.version_number = versionNumber;
        }
        onSubmit(params);
    };

    return (
        <form onSubmit={handleSubmit}>
            <div className={layout.fieldGrid}>
                <div className={layout.field}>
                    <MarkLabel
                        htmlFor="dev-target-type"
                        style={{
                            marginBottom: '0.4rem'
                        }}
                    >
                        Target type
                    </MarkLabel>
                    <MarkSelect
                        id="dev-target-type"
                        value={targetType}
                        onChange={(e) => setTargetType(e.currentTarget.value)}
                    >
                        <option value="blast_variant">blast_variant</option>
                        <option value="drip_step">drip_step</option>
                    </MarkSelect>
                </div>
                {targetType === 'blast_variant' ? (
                    <>
                        <div className={layout.field}>
                            <MarkLabel
                                htmlFor="dev-blast-id"
                                style={{
                                    marginBottom: '0.4rem'
                                }}
                            >
                                Blast ID
                            </MarkLabel>
                            <MarkInput
                                id="dev-blast-id"
                                type="number"
                                value={blastId}
                                onChange={(e) => setBlastId(e.currentTarget.value)}
                            />
                        </div>
                        <div className={layout.field}>
                            <MarkLabel
                                htmlFor="dev-variant-label"
                                style={{
                                    marginBottom: '0.4rem'
                                }}
                            >
                                Variant label
                            </MarkLabel>
                            <MarkInput
                                id="dev-variant-label"
                                type="text"
                                value={variantLabel}
                                onChange={(e) => setVariantLabel(e.currentTarget.value)}
                            />
                        </div>
                    </>
                ) : (
                    <>
                        <div className={layout.field}>
                            <MarkLabel
                                htmlFor="dev-drip-id"
                                style={{
                                    marginBottom: '0.4rem'
                                }}
                            >
                                Drip ID
                            </MarkLabel>
                            <MarkInput
                                id="dev-drip-id"
                                type="number"
                                value={dripId}
                                onChange={(e) => setDripId(e.currentTarget.value)}
                            />
                        </div>
                        <div className={layout.field}>
                            <MarkLabel
                                htmlFor="dev-step-id"
                                style={{
                                    marginBottom: '0.4rem'
                                }}
                            >
                                Step ID
                            </MarkLabel>
                            <MarkInput
                                id="dev-step-id"
                                type="text"
                                value={stepId}
                                onChange={(e) => setStepId(e.currentTarget.value)}
                            />
                        </div>
                        <div className={layout.field}>
                            <MarkLabel
                                htmlFor="dev-version-number"
                                style={{
                                    marginBottom: '0.4rem'
                                }}
                            >
                                Version number (optional)
                            </MarkLabel>
                            <MarkInput
                                id="dev-version-number"
                                type="number"
                                value={versionNumber}
                                onChange={(e) => setVersionNumber(e.currentTarget.value)}
                            />
                        </div>
                    </>
                )}
                <div className={layout.field}>
                    <MarkLabel
                        htmlFor="dev-channel"
                        style={{
                            marginBottom: '0.4rem'
                        }}
                    >
                        Channel
                    </MarkLabel>
                    <MarkSelect
                        id="dev-channel"
                        value={channel}
                        onChange={(e) => setChannel(e.currentTarget.value)}
                    >
                        <option value="email">email</option>
                        <option value="sms">sms</option>
                    </MarkSelect>
                </div>
                <MarkButton
                    type="submit"
                    style={{
                        alignSelf: 'flex-start'
                    }}
                    variant="primary"
                >
                    Load template
                </MarkButton>
            </div>
        </form>
    );
};
export default DevTemplateEditor;
